// src/pages/Dashboard/Admin/AdherentPret/components/EditPretDrawer.jsx

import React, { useEffect, useMemo, useState } from "react";
import {
  FaCalendarCheck,
  FaEraser,
  FaSave,
  FaTimes,
  FaTrash,
} from "react-icons/fa";

import {
  formatDateLong,
  formatDateShort,
  getArticlePhotoSrc,
  getPretAdherentName,
  getPretArticleName,
  getPretStatus,
  getPretStatusClass,
  getPretStatusLabel,
  todayStr,
  toDateInputValue,
} from "../utils/adherentPretDashboardUtils";

const initialForm = {
  date_remise: "",
  note_pret: "",
};

export default function EditPretDrawer({
  isOpen,
  onClose,
  pret,
  onSubmit,
  onDelete,
  saving,
  deletingId,
}) {
  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    if (!isOpen || !pret) return;

    setFormData({
      date_remise: pret?.date_remise ? toDateInputValue(pret.date_remise) : "",
      note_pret: pret?.note_pret || "",
    });
  }, [isOpen, pret]);

  useEffect(() => {
    if (!isOpen) return;

    const root = document.documentElement;
    const previousOverflow = root.style.overflow;
    root.style.overflow = "hidden";

    const onEscape = (event) => {
      if (event.key === "Escape" && !saving) {
        onClose?.();
      }
    };

    window.addEventListener("keydown", onEscape);

    return () => {
      root.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onEscape);
    };
  }, [isOpen, saving, onClose]);

  const isDirty = useMemo(() => {
    if (!pret) return false;

    return (
      String(formData.date_remise || "") !==
        String(toDateInputValue(pret?.date_remise) || "") ||
      String(formData.note_pret || "") !== String(pret?.note_pret || "")
    );
  }, [formData, pret]);

  const updateField = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClose = () => {
    if (saving) return;

    if (!isDirty || window.confirm("Des modifications ne sont pas enregistrées. Fermer ?")) {
      onClose?.();
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!pret?.id) return;

    await onSubmit?.(pret.id, formData);
  };

  if (!isOpen || !pret) return null;

  const status = formData.date_remise ? "returned" : getPretStatus(pret);

  return (
    <div className="fixed inset-0 z-[80]">
      <div
        className="absolute inset-0 bg-slate-950/50 backdrop-blur-sm"
        onClick={handleClose}
        aria-hidden
      />

      <aside className="absolute right-0 top-0 flex h-full w-full max-w-2xl flex-col bg-white shadow-2xl dark:bg-slate-900">
        <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

        <div className="flex items-start justify-between gap-4 border-b border-slate-200 p-5 dark:border-slate-800">
          <div>
            <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
              Modification
            </p>

            <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
              Modifier le prêt
            </h2>

            <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
              Indiquez une date de remise pour clôturer le prêt ou laissez vide
              pour conserver le statut en cours.
            </p>
          </div>

          <button
            type="button"
            onClick={handleClose}
            disabled={saving}
            className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
          >
            <FaTimes />
          </button>
        </div>

        <form
          id="edit-pret-form"
          onSubmit={handleSubmit}
          className="flex-1 space-y-5 overflow-y-auto p-5"
        >
          <div className="rounded-3xl border border-slate-200 bg-slate-50 p-4 dark:border-slate-800 dark:bg-slate-800/50">
            <div className="flex items-start gap-4">
              <img
                src={getArticlePhotoSrc(pret?.article?.photo_url)}
                alt={getPretArticleName(pret)}
                className="h-20 w-20 shrink-0 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                onError={(event) => {
                  event.currentTarget.src = "/img/no-image.jpg";
                }}
              />

              <div className="min-w-0 flex-1">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Adhérent
                    </p>
                    <p className="font-bold text-slate-900 dark:text-white">
                      {getPretAdherentName(pret) || "—"}
                    </p>
                  </div>

                  <span
                    className={`inline-flex w-fit rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${getPretStatusClass(
                      status
                    )}`}
                  >
                    {formData.date_remise
                      ? `Rendu le ${formatDateShort(formData.date_remise)}`
                      : getPretStatusLabel(status)}
                  </span>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Matériel
                    </p>
                    <p className="font-bold text-slate-900 dark:text-white">
                      {getPretArticleName(pret)}
                    </p>
                  </div>

                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                      Date du prêt
                    </p>
                    <p className="font-bold text-slate-900 dark:text-white">
                      {formatDateLong(pret?.date_pret)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Date de remise
            </label>

            <div className="grid grid-cols-1 gap-2 sm:grid-cols-[1fr,auto,auto]">
              <input
                type="date"
                value={formData.date_remise}
                onChange={(event) =>
                  updateField("date_remise", event.target.value)
                }
                min={toDateInputValue(pret?.date_pret) || undefined}
                className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
              />

              <button
                type="button"
                onClick={() => updateField("date_remise", todayStr())}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <FaCalendarCheck />
                Aujourd’hui
              </button>

              <button
                type="button"
                onClick={() => updateField("date_remise", "")}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <FaEraser />
                Effacer
              </button>
            </div>

            <p className="mt-2 text-xs leading-5 text-slate-500 dark:text-slate-400">
              Laisser vide signifie que le matériel est toujours en cours de
              prêt.
            </p>
          </div>

          <div>
            <label className="mb-1 block text-xs font-bold uppercase tracking-wide text-slate-500 dark:text-slate-400">
              Note
            </label>

            <textarea
              value={formData.note_pret}
              onChange={(event) => updateField("note_pret", event.target.value)}
              rows={5}
              placeholder="État restitué, accessoires manquants, observations..."
              className="w-full resize-none rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200"
            />
          </div>
        </form>

        <div className="border-t border-slate-200 bg-white/95 p-4 backdrop-blur dark:border-slate-800 dark:bg-slate-900/95">
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-between">
            <button
              type="button"
              onClick={() => onDelete?.(pret)}
              disabled={deletingId === pret.id || saving}
              className="inline-flex items-center justify-center gap-2 rounded-2xl border border-rose-200 bg-rose-50 px-4 py-2 text-sm font-bold text-rose-700 transition hover:bg-rose-100 disabled:opacity-60 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
            >
              <FaTrash />
              Supprimer
            </button>

            <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={handleClose}
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200"
              >
                <FaTimes />
                Annuler
              </button>

              <button
                type="submit"
                form="edit-pret-form"
                disabled={saving}
                className="inline-flex items-center justify-center gap-2 rounded-2xl bg-[#013E8E] px-4 py-2 text-sm font-bold text-white transition hover:bg-[#012f6c] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <FaSave />
                {saving ? "Enregistrement..." : "Enregistrer"}
              </button>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
}